import { createContext, useCallback, useContext, useMemo } from 'react';
import { get } from 'lodash';

import { useTrackEvent } from '@/lib/analytics';

import {
  isCellWithPipeline,
  isCellWithVisualisations,
  isChatCell,
  isConversationCell,
  isInvalidCell,
  isRecordsLikeCell,
} from '@/core/cell';
import { convertKeysToSnakeCase, convertToYaml } from '@/lib/utils/object';
import { isConversationPart } from '@/core/cell';

import {
  deleteExplorationCell,
  getExplorationCellIndex,
  setExplorationCells,
} from '@/core/exploration';

import { Cell, CellViewOptions, Visualisation } from '../types';
import { QueryMeta, useExplorationContext } from './exploration-context';
import { useExplorationUIContext } from './exploration-ui-context';
import { dereferencePipeline, flattenPipeline } from '../pipeline/utils';
import { generateVisualisation } from '../components/visualisation/utils';
import { useMetadataContext } from '../metadata-context';
import { removeViewOption, setViewOption, setViewOptions } from './utils/view-options';
import { ensureValidPipeline } from '../utils';
import { extractCellFromExploration } from './utils';
import {
  getCellRow,
  getCellRows,
  updateCellRowCollapsed,
  updateCellRowHeight,
} from './exploration-layout/utils';

import { restoreInvalidOperations } from '../output';
import { useConversationExplorationContext } from '../conversation/conversation-exploration-context';

export interface ExplorationCellContextValue {
  cell: Cell;
  cellIndex: number;
  isSelectedCell: boolean;
  isConversationPart: boolean;
  isConversationSubject: boolean;
  rowContainsConversationPart: boolean;
  setCell: (cell: Cell) => void;
  setQueryMeta: (meta: QueryMeta) => void;
  setCellViewOption: <T extends keyof CellViewOptions>(
    key: T,
    value: CellViewOptions[T] | undefined,
  ) => void;
  getCellViewOption: <T extends keyof CellViewOptions>(key: T) => CellViewOptions[T]; // Json | undefined;
  isCollapsed: boolean;
  isCollapsible: boolean;
  toggleCollapsed: () => void;
  addVisualization: (visualisation: Visualisation) => void;
  addDefaultVisualization: () => void;
  startConversation: () => void;
  endConversation: () => void;
  setVisualisation: (idx: number, visualisation: Visualisation) => void;
  removeVisualisation: (idx: number) => void;
  isTableVisible: boolean;
  hideTable: () => void;
  showTable: () => void;
  isPipelinePreviewVisible: boolean;
  isBlockTitleVisible: boolean;
  tableMode: 'table' | 'cards';
  getYaml: () => string;
  copyCell: () => void;
}

const defaultContextValue = Symbol();

const ExplorationCellContext = createContext<
  ExplorationCellContextValue | typeof defaultContextValue
>(defaultContextValue);

export const useExplorationCellContext = () => {
  const context = useContext(ExplorationCellContext);
  if (context === defaultContextValue) {
    throw new Error(
      'useExplorationCellContext must be used within a ExplorationCellContextProvider',
    );
  }
  return context;
};

const TableVisibilityKey = 'tableVisibility';
export const TableModeKey = 'viewMode';
export const PipelinePreviewKey = 'pipelinePreviewVisibility';
export const BlockTitleKey = 'blockTitleVisibility' as const;

export const ExplorationCellContextProvider = ({
  cell,
  isCollapsible,
  enforcedRowCollapse,
  children,
}: {
  cell: Cell;
  isCollapsible: boolean;
  enforcedRowCollapse: boolean;
  children: React.ReactNode;
}) => {
  const {
    exploration,
    setCellById,
    setQueryMeta,
    getVariables,
    setExploration,
    persistCopiedCell,
    isConversationSubject,
  } = useExplorationContext();
  const { isWithinConversation } = useConversationExplorationContext();
  const { selectedCell, closeEditor, setNewConversationSubjectId } = useExplorationUIContext();

  const { models, metrics } = useMetadataContext();
  const trackEvent = useTrackEvent();

  const isSelectedCell = selectedCell?.id === cell.id;
  const cellIndex = getExplorationCellIndex(exploration, cell.id);

  const cellIsPartOfConversation = isConversationPart(cell) || isWithinConversation;
  const rowContainsConversationPart = exploration.view.cells.some(
    (c) =>
      c.viewOptions?.rowId !== undefined &&
      c.viewOptions.rowId === cell.viewOptions?.rowId &&
      isConversationPart(c),
  );

  const wrappedSetCell = useCallback(
    (cell: Cell) => {
      setCellById(cell, cell.id);
    },
    [setCellById],
  );

  const wrappedSetQueryMeta = useCallback(
    (meta: QueryMeta | null) => setQueryMeta({ cellId: cell.id, meta }),
    [cell, setQueryMeta],
  );

  const setCellViewOption = <T extends keyof CellViewOptions>(
    key: T,
    value: CellViewOptions[T] | undefined,
  ) => {
    wrappedSetCell(setViewOption(cell, key, value));
  };

  const getCellViewOption = useCallback(
    <T extends keyof CellViewOptions>(key: T) => get(cell.viewOptions, key, undefined),
    [cell.viewOptions],
  );

  const resetCellHeightAndShowTable = () => {
    wrappedSetCell(
      setViewOptions(removeViewOption(cell, TableVisibilityKey), { height: undefined }),
    );
  };

  const resetMultiCellRowHeightAndShowTable = () => {
    const cellwithTable = removeViewOption(cell, 'tableVisibility');
    const cells = [...exploration.view.cells];
    cells[cellIndex] = cellwithTable;
    setExploration({
      ...exploration,
      view: {
        ...exploration.view,
        cells: updateCellRowHeight(cells, cellIndex, undefined),
      },
    });
  };

  const isCollapsed = useMemo(() => {
    const cellRow = getCellRow(getCellRows(exploration.view.cells), cellIndex);
    if (cellRow.length > 1 && enforcedRowCollapse) {
      return cellRow.every((cell) => get(cell.viewOptions, 'collapsed') === 'yes');
    }
    return getCellViewOption('collapsed') === 'yes';
  }, [getCellViewOption, cellIndex, enforcedRowCollapse, exploration.view.cells]);

  const toggleCollapsed = () => {
    if (isSelectedCell && !isCollapsed) {
      closeEditor();
    }

    if (enforcedRowCollapse) {
      // Toggle entire row on desktop
      const cells = [...exploration.view.cells];
      return setExploration({
        ...exploration,
        view: {
          ...exploration.view,
          cells: updateCellRowCollapsed(cells, cellIndex, !isCollapsed),
        },
      });
    }

    const updatedCell: CellViewOptions = { collapsed: isCollapsed ? 'no' : 'yes' };
    if (!isCollapsed) {
      updatedCell.height = undefined;
    }
    wrappedSetCell(setViewOptions(cell, updatedCell));
  };

  const isTableVisible = getCellViewOption(TableVisibilityKey) !== 'hidden';
  const hideTable = () => setCellViewOption(TableVisibilityKey, 'hidden');
  const showTable = () => {
    const isFromMultiCellRow =
      getCellRow(getCellRows(exploration.view.cells), cellIndex).length > 1;
    return isFromMultiCellRow
      ? resetMultiCellRowHeightAndShowTable()
      : resetCellHeightAndShowTable();
  };

  const tableMode = getCellViewOption(TableModeKey) === 'cards' ? 'cards' : 'table';

  const isPipelinePreviewVisible = getCellViewOption(PipelinePreviewKey) !== 'hidden';

  const isBlockTitleVisible = getCellViewOption(BlockTitleKey) !== 'hidden';

  const setVisualisations = (visualisations: Visualisation[]) => {
    if (!isRecordsLikeCell(cell)) {
      throw new Error(`Cannot set visualisations to cell of kind ${cell.kind}`);
    }

    const updatedCell =
      !isTableVisible && visualisations.length === 0
        ? removeViewOption(cell, TableVisibilityKey)
        : cell;

    wrappedSetCell({
      ...updatedCell,
      visualisations,
    });
  };

  const removeVisualisation = (idx: number) => {
    if (!isRecordsLikeCell(cell)) {
      return;
    }

    const visualisations = isCellWithVisualisations(cell) ? (cell.visualisations ?? []) : [];
    const { baseModelId, operations } = flattenPipeline(cell.pipeline, exploration);
    const analyticsProps = { baseModelId, operations, visualisations };
    const removingVisualisation = visualisations[idx];

    trackEvent('Visualisation Removed', {
      removingVisualisation,
      ...analyticsProps,
    });

    setVisualisations([...visualisations.slice(0, idx), ...visualisations.slice(idx + 1)]);
  };

  const setVisualisation = (idx: number, visualisation: Visualisation) => {
    if (
      !isRecordsLikeCell(cell) ||
      !isCellWithVisualisations(cell) ||
      cell.visualisations === undefined
    ) {
      return;
    }

    const visualisations = isCellWithVisualisations(cell) ? (cell.visualisations ?? []) : [];
    const { baseModelId, operations } = flattenPipeline(cell.pipeline, exploration);
    const analyticsProps = { baseModelId, operations, visualisations };

    trackEvent('Visualisation Changed', {
      visualisation,
      ...analyticsProps,
    });

    const updatedVisualisatios = [
      ...visualisations.slice(0, idx),
      visualisation,
      ...visualisations.slice(idx + 1),
    ];

    setVisualisations(updatedVisualisatios);
  };

  const addVisualization = (visualisation: Visualisation) => {
    if (!isRecordsLikeCell(cell)) {
      throw new Error(`Cannot add visualisation to cell of kind ${cell.kind}`);
    }
    setVisualisations([...(cell.visualisations ?? []), visualisation]);
  };

  const addDefaultVisualization = () => {
    if (!isRecordsLikeCell(cell)) {
      throw new Error(`Cannot add visualisation to cell of kind ${cell.kind}`);
    }
    addVisualization(
      generateVisualisation(
        ensureValidPipeline(cell.pipeline, {
          models,
          metrics,
          variables: getVariables(),
          exploration,
        }),
        { models, variables: getVariables(), metrics, exploration },
      ),
    );
  };

  const wrappedStartConversation = () => {
    if (isInvalidCell(cell)) {
      return;
    }

    setNewConversationSubjectId(cell.id);
  };

  const endConversation = () => {
    if (isConversationCell(cell)) {
      setNewConversationSubjectId(null);
      setExploration(
        deleteExplorationCell(
          setExplorationCells(
            exploration,
            exploration.view.cells.map((c) => {
              if (isConversationCell(c) || isInvalidCell(c) || isChatCell(c)) {
                return c;
              }
              if ('conversationId' in c) {
                const { conversationId, ...rest } = c;
                return rest;
              }
              return c;
            }),
          ),
          cell.id,
        ),
      );
    }
  };

  const getYaml = () => {
    if (!isCellWithPipeline(cell)) {
      return '';
    }

    return convertToYaml(
      convertKeysToSnakeCase({
        pipeline: dereferencePipeline(restoreInvalidOperations(cell.pipeline), exploration),
        ...(isCellWithVisualisations(cell) ? { visualisations: cell.visualisations } : {}),
      }),
    );
  };

  const copyCell = () => {
    const extractedCell = extractCellFromExploration(cell.id, exploration);

    if ('conversationId' in extractedCell.cell) {
      delete extractedCell.cell.conversationId;
    }

    persistCopiedCell(extractedCell);
  };

  return (
    <ExplorationCellContext.Provider
      value={{
        cell,
        cellIndex,
        isSelectedCell,
        isConversationSubject: isConversationSubject(cell),
        isConversationPart: cellIsPartOfConversation,
        rowContainsConversationPart,
        setCell: wrappedSetCell,
        setQueryMeta: wrappedSetQueryMeta,
        setCellViewOption,
        getCellViewOption,
        isCollapsed: isCollapsed && isCollapsible,
        isCollapsible,
        toggleCollapsed,
        addVisualization,
        addDefaultVisualization,
        startConversation: wrappedStartConversation,
        endConversation,
        setVisualisation,
        removeVisualisation,
        isTableVisible,
        hideTable,
        showTable,
        tableMode,
        isPipelinePreviewVisible,
        isBlockTitleVisible,
        getYaml,
        copyCell,
      }}>
      {children}
    </ExplorationCellContext.Provider>
  );
};
